import { useState } from "react";

import capitalize from "lodash/capitalize";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { ItemIcon, PlainTooltip, Type } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { DEFAULT_COMPANY_QUOTA_CREDIT } from "app/pages/sales-intelligence/modules/company-autocomplete/constants";
import highlightQueryWords from "app/pages/sales-intelligence/modules/company-autocomplete/utils/highlightQueryWords";
import QuotaTag from "app/pages/sales-intelligence/modules/quota/components/QuotaTag";
import NotEnoughQuotaTooltipContainer from "app/pages/sales-intelligence/modules/quota/containers/NotEnoughQuotaTooltipContainer/NotEnoughQuotaTooltipContainer";
import type { CompanyInformationQuota } from "app/pages/sales-intelligence/modules/quota/types";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import type { IDomainItem } from "app/@types/autocomplete";

import { StyledAutocompleteItemText, StyledAutocompleteItemTextWrapper } from "../CompanyAutocompleteItem/styles";

import { StyledAutocompleteDivider, StyledAutocompleteDomainWrapper } from "./styles";

const DomainAutocompleteItem = ({
    domain,
    queryWords,
    isFirst,
    isLast,
    onItemClick,
    hideLabel = false,
    isLocked = false,
    hasCompanyData,
    isHideDataCreditChips = false,
    isPurchased = false,
    companyQuota,
}: {
    domain: IDomainItem;
    queryWords: string[];
    isFirst: boolean;
    isLast: boolean;
    onItemClick?: (domain: string) => void;
    hideLabel?: boolean;
    isLocked?: boolean;
    hasCompanyData?: boolean;
    isHideDataCreditChips?: boolean;
    isPurchased?: boolean;
    companyQuota?: CompanyInformationQuota;
}) => {
    const [isTooltipEnabled, setIsTooltipEnabled] = useState(false);
    const isDataCreditsEnabled = salesSettingsHelper.isDataCreditsEnabled;

    const setTextRef = (node: HTMLDivElement | null) => {
        if (node) {
            setIsTooltipEnabled(node.scrollWidth > node.clientWidth);
        }
    };

    const handleDomainClick = () => {
        if (!hasCompanyData) {
            return;
        }
        onItemClick?.(domain.name);
    };

    const domainRow = (
        <StyledAutocompleteDomainWrapper
            key={domain.name}
            onClick={handleDomainClick}
            isFirst={isFirst}
            isLast={isLast}
            isDisabled={isLocked || !hasCompanyData}
        >
            <ItemIcon
                iconType={Type.Website}
                iconSrc={domain.image || ""}
            />
            <PlainTooltip
                tooltipContent={domain.name}
                placement="top"
                enabled={isTooltipEnabled}
            >
                <StyledAutocompleteItemTextWrapper>
                    <StyledAutocompleteItemText
                        ref={setTextRef}
                        isDisabled={!hasCompanyData}
                    >
                        {hasCompanyData ? highlightQueryWords(domain.name, queryWords) : domain.name}
                    </StyledAutocompleteItemText>
                </StyledAutocompleteItemTextWrapper>
            </PlainTooltip>
            {hideLabel ? null : (
                <>
                    <StyledAutocompleteDivider />
                    <Text
                        fontVariant="listItem3"
                        color={hasCompanyData ? colorsPalettes.carbon[250] : colorsPalettes.carbon[200]}
                    >
                        {capitalize(translate("common.website"))}
                    </Text>
                </>
            )}
            <Flex
                gap="8px"
                margin="0 0 0 auto"
                alignItems="center"
            >
                {hasCompanyData ? null : (
                    <Text
                        fontVariant="listItem3"
                        color={colorsPalettes.carbon[250]}
                    >
                        {capitalize(
                            translate(
                                hasCompanyData ? "common.website" : "si.pages.home.search.autocomplete.no_data.website",
                            ),
                        )}
                    </Text>
                )}
                {isDataCreditsEnabled && !isHideDataCreditChips && !isPurchased ? (
                    <QuotaTag
                        size="sm"
                        type={isLocked ? "warning" : "light"}
                        title={DEFAULT_COMPANY_QUOTA_CREDIT}
                    />
                ) : null}
                {!isDataCreditsEnabled && isLocked ? (
                    <SWReactIcons
                        iconName="locked2"
                        size="sm"
                        iconColor={colorsPalettes.carbon[300]}
                    />
                ) : null}
            </Flex>
        </StyledAutocompleteDomainWrapper>
    );

    return isLocked && companyQuota ? (
        <NotEnoughQuotaTooltipContainer quota={companyQuota}>
            {domainRow}
        </NotEnoughQuotaTooltipContainer>
    ) : (
        domainRow
    );
};

export default DomainAutocompleteItem;
